import React, { useState, useEffect } from 'react';
import { 
  Building, Save, Upload, Trash2, AlertCircle, CheckCircle, X, Eye
} from 'lucide-react';
import useCompanyParams from '../hooks/useCompanyParams';
import { Toast } from '../components/ui/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import { useLoader } from '../contexts/LoaderContext';

const CompanySettings = () => {
  const { 
    companyInfo, 
    handleInputChange, 
    updateCompanyParams,
    uploadLogo,
    deleteLogo,
    loading: companyLoading 
  } = useCompanyParams();
  

  const { showLoader, hideLoader } = useLoader();

  useEffect(() => {
    if (companyLoading) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [companyLoading]);


  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [logoPreview, setLogoPreview] = useState(companyInfo.logo_url);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // ============================================================================
  // TOAST HELPER
  // ============================================================================
  
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, message: '', type: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);
  
  // ============================================================================
  // LOGO URL AVEC BACKEND
  // ============================================================================
  
 // ✅ Mettre à jour l'aperçu du logo quand companyInfo change
  useEffect(() => {
    setLogoPreview(companyInfo.logo_url);
  }, [companyInfo.logo_url]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validation
    if (file.size > 2 * 1024 * 1024) {
      showToast('Le fichier est trop volumineux (max 2MB)', 'error');
      return;
    }

    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'].includes(file.type)) {
      showToast('Format non supporté (PNG, JPG, SVG uniquement)', 'error');
      return;
    }

    // Aperçu local immédiat
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload
    handleLogoUpload(file);
  };

  const handleLogoUpload = async (file) => {
    setUploading(true);
    
    try {
      const result = await uploadLogo(file);
      
      if (result.success) {
        showToast('Logo uploadé avec succès !', 'success');
      } else {
        showToast(result.error || 'Erreur lors de l\'upload', 'error');
      }
    } catch (error) {
      showToast('Erreur lors de l\'upload du logo', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteLogo = async () => {
    try {
      const result = await deleteLogo();
      
      if (result.success) {
        setLogoPreview(null);
        showToast('Logo supprimé avec succès !', 'success');
      } else {
        showToast(result.error || 'Erreur lors de la suppression', 'error');
      }
    } catch (error) {
      showToast('Erreur lors de la suppression du logo', 'error');
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      const result = await updateCompanyParams(companyInfo);
      
      if (result.success) {
        showToast('Paramètres sauvegardés avec succès !', 'success');
      } else {
        showToast(result.error || 'Erreur lors de la sauvegarde', 'error');
      }
    } catch (error) {
      showToast('Erreur de connexion au serveur', 'error');
    } finally {
      setSaving(false);
    }
  };

 
  if (companyLoading) {
    return null;
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      {/* Header */}
      <div className="px-6 py-4 mb-5 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
        <Building size={24} className="text-white" />
        <h2 className="font-bold text-white text-xl">Informations de l'entreprise</h2>
      </div>

      {/* Logo */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Logo de l'entreprise
        </label>
        <div className="flex items-start gap-6">
          <div className="w-32 h-32 rounded-xl border-2 border-gray-200 flex items-center justify-center shadow-sm p-2"
               style={{ backgroundColor: '#f5f3eb' }}>
            {uploading ? (
              <div className="animate-spin w-12 h-12 border-4 rounded-full" 
                   style={{ borderColor: '#448696', borderTopColor: 'transparent' }}></div>
            ) : logoPreview ? (
              <img src={logoPreview} alt="Logo" className="max-w-full max-h-full object-contain rounded-lg" />
            ) : (
              <div 
                className="w-full h-full flex items-center justify-center text-3xl font-bold rounded-lg"
                style={{ color: companyInfo.couleur_logo || '#448696' }}
              >
                {companyInfo.logo_initiales || 'HP'}
              </div>
            )}
          </div>
          
          <div>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              className="hidden"
              id="company-logo-upload"
              disabled={uploading}
            />
            <label
              htmlFor="company-logo-upload"
              className={`inline-flex items-center px-4 py-3 text-white rounded-xl font-semibold cursor-pointer shadow-lg hover:shadow-xl transition-all ${
                uploading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              style={{ backgroundColor: '#448696' }}
            >
              <Upload className="w-4 h-4 mr-2" />
              <span>{uploading ? 'Upload...' : 'Choisir un logo'}</span>
            </label>
            
            {logoPreview && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="ml-2 px-4 py-3 bg-red-50 text-red-700 rounded-xl text-sm font-semibold hover:bg-red-100 transition-all inline-flex items-center"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </button>
            )}
            
            <p className="mt-2 text-xs font-medium text-gray-600">
              PNG, JPG ou SVG (max 2 Mo)
            </p>
          </div>
        </div>
      </div>

      {/* Grille d'informations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Nom de l'entreprise *</label>
          <input
            type="text"
            value={companyInfo.nom}
            onChange={(e) => handleInputChange('nom', e.target.value)}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none focus:border-[#448696] focus:ring-2 focus:ring-[#448696]/20"
            placeholder="Nom de votre entreprise"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
          <input
            type="email"
            value={companyInfo.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none focus:border-[#448696] focus:ring-2 focus:ring-[#448696]/20"
            placeholder="contact@entreprise.com"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Téléphone</label>
          <input
            type="tel"
            value={companyInfo.telephone}
            onChange={(e) => handleInputChange('telephone', e.target.value)}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none focus:border-[#448696] focus:ring-2 focus:ring-[#448696]/20"
            placeholder="+242 06 123 45 67"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Site web</label>
          <input
            type="url"
            value={companyInfo.site_web}
            onChange={(e) => handleInputChange('site_web', e.target.value)}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none focus:border-[#448696] focus:ring-2 focus:ring-[#448696]/20"
            placeholder="https://www.entreprise.com"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-gray-700 mb-2">Adresse</label>
          <input
            type="text"
            value={companyInfo.adresse}
            onChange={(e) => handleInputChange('adresse', e.target.value)}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none focus:border-[#448696] focus:ring-2 focus:ring-[#448696]/20"
            placeholder="Adresse complète"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Ville</label>
          <input
            type="text"
            value={companyInfo.ville}
            onChange={(e) => handleInputChange('ville', e.target.value)}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none focus:border-[#448696] focus:ring-2 focus:ring-[#448696]/20"
            placeholder="Brazzaville"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Pays</label>
          <input
            type="text"
            value={companyInfo.pays}
            onChange={(e) => handleInputChange('pays', e.target.value)}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none focus:border-[#448696] focus:ring-2 focus:ring-[#448696]/20"
            placeholder="Congo"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">RCCM</label>
          <input
            type="text"
            value={companyInfo.rccm}
            onChange={(e) => handleInputChange('rccm', e.target.value)}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none focus:border-[#448696] focus:ring-2 focus:ring-[#448696]/20"
            placeholder="CG-BZV-01-2023-B12-00123"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">NIF</label>
          <input
            type="text"
            value={companyInfo.nif}
            onChange={(e) => handleInputChange('nif', e.target.value)}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none focus:border-[#448696] focus:ring-2 focus:ring-[#448696]/20"
            placeholder="M123456789"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Initiales du logo *</label>
          <input
            type="text"
            value={companyInfo.logo_initiales}
            onChange={(e) => handleInputChange('logo_initiales', e.target.value)}
            maxLength="10"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none focus:border-[#448696] focus:ring-2 focus:ring-[#448696]/20"
            placeholder="HP"
          />
          <p className="text-xs font-medium text-gray-600 mt-1">
            Utilisées si pas d'image de logo
          </p>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
          <textarea
            value={companyInfo.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            rows="4"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none focus:border-[#448696] focus:ring-2 focus:ring-[#448696]/20 resize-none"
            placeholder="Description de votre entreprise..."
          />
        </div>
      </div>

      {/* Aperçu dans la sidebar */}
      <div className="mt-8 p-5 rounded-xl border-2 border-gray-100" style={{ backgroundColor: '#f5f3eb' }}>
        <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <Eye size={16} />
          Aperçu dans la sidebar
        </h3>
        
        <div className="rounded-xl p-6" style={{ backgroundColor: '#448696' }}>
          <div className="flex items-center gap-3">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 p-1 shadow-lg"
              style={{ backgroundColor: '#ffffff' }}
            >
              {logoPreview ? (
                <img 
                  src={logoPreview}
                  alt="Logo"
                  className="max-w-full max-h-full object-contain rounded-lg"
                />
              ) : (
                <div 
                  className="w-full h-full flex items-center justify-center font-bold text-xl rounded-lg"
                  style={{ color: companyInfo.couleur_logo || '#448696' }}
                >
                  {companyInfo.logo_initiales || 'HP'}
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-white truncate leading-tight">
                {companyInfo.nom || 'HoraPilot'}
              </h1>
              <p className="text-sm text-white/70 font-medium truncate">
                {companyInfo.description || 'Gestion RH'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bouton de sauvegarde */}
      <div className="mt-8 flex justify-end gap-3">
        <button
          onClick={handleSave}
          disabled={saving || !companyInfo.nom}
          className="px-6 py-3 text-white rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          style={{ backgroundColor: '#448696' }}
        >
          {saving ? (
            <>
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
              Enregistrement...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Enregistrer les modifications
            </>
          )}
        </button>
      </div>

      {/* ConfirmDialog pour suppression logo */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteLogo}
        title="Supprimer le logo ?"
        message="Êtes-vous sûr de vouloir supprimer le logo de l'entreprise ? Cette action est irréversible."
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />

      {/* Toast */}
      {toast.show && (
        <Toast
          show={toast.show}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: '' })}
        />
      )}
    </div>
  );
};

export default CompanySettings;